class GiftWrapDrawer {
  constructor() {
    this.isOpen = false;
    this.activeTrigger = null;
    this.isSheetOpen = false;
    this.activeSheetTrigger = null;
    this.pendingVariantIds = [];
    this.pendingRemoval = null;
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.startSheetDrag = this.startSheetDrag.bind(this);
    this.moveSheetDrag = this.moveSheetDrag.bind(this);
    this.endSheetDrag = this.endSheetDrag.bind(this);

    this.sheetPlaceholder = null;

    document.addEventListener("click", this.handleDocumentClick);
    document.addEventListener("keydown", this.handleKeyDown);
    document.addEventListener("change", this.handleVariantChange);
    this.initSheetDrag();
    this.updateContinueButtonState();
    this.setStatus("");
  }

  get drawer() {
    return document.querySelector("[data-gift-drawer]");
  }

  get panel() {
    return this.drawer?.querySelector("[data-gift-panel]");
  }

  get sheet() {
    return document.querySelector("[data-gift-sheet]");
  }

  get sheetContent() {
    return document.querySelector("[data-gift-sheet-content]");
  }

  get sheetBackdrop() {
    return document.querySelector(".brn-gift-sheet__backdrop");
  }

  get sheetHandle() {
    return document.querySelector("[data-gift-sheet-handle]");
  }

  get continueButton() {
    return this.drawer?.querySelector("[data-gift-continue]");
  }

  get selectedVariantInputs() {
    return Array.from(this.drawer?.querySelectorAll('input[name="gift-wrap-option"]:checked') || []);
  }

  get statusElement() {
    return this.drawer?.querySelector("[data-gift-status]");
  }

  get cartDrawer() {
    return document.querySelector("cart-drawer");
  }

  get giftSummary() {
    return document.querySelector("[data-gift-summary]");
  }

  getSectionsToRender() {
    const cartDrawer = this.cartDrawer;
    if (!cartDrawer || typeof cartDrawer.getSectionsToRender !== "function") {
      return [];
    }
    return cartDrawer.getSectionsToRender().map((section) => section.id);
  }

  refreshCart(parsedState) {
    const cartDrawer = this.cartDrawer;
    if (!cartDrawer || typeof cartDrawer.renderContents !== "function") return;
    cartDrawer.renderContents(parsedState, false);
  }

  setStatus(message = "") {
    const status = this.statusElement;
    if (!status) return;

    if (message) {
      status.textContent = message;
      status.removeAttribute("hidden");
    } else {
      status.textContent = "";
      status.setAttribute("hidden", "true");
    }
  }

  setButtonLoading(button, loading) {
    if (!button) return;
    if (loading) {
      button.disabled = true;
      button.setAttribute("aria-disabled", "true");
      button.classList.add("is-loading");
    } else {
      button.classList.remove("is-loading");
      this.updateContinueButtonState();
    }
  }

  clearSelection() {
    const inputs = this.drawer?.querySelectorAll('input[name="gift-wrap-option"]');
    if (!inputs?.length) return;
    inputs.forEach((input) => {
      input.checked = false;
    });
    this.updateContinueButtonState();
  }

  applyPendingSelection() {
    if (!this.pendingVariantIds?.length) return;

    const inputs = this.drawer?.querySelectorAll('input[name="gift-wrap-option"]');
    if (!inputs?.length) {
      this.pendingVariantIds = [];
      return;
    }

    let matched = false;
    inputs.forEach((input) => {
      const shouldCheck = this.pendingVariantIds.includes(input.value);
      input.checked = shouldCheck;
      if (shouldCheck) matched = true;
    });

    this.pendingVariantIds = [];

    if (matched) {
      this.updateContinueButtonState();
    }
  }

  getFocusableElements() {
    const container = this.isSheetOpen ? this.sheetContent : this.panel;
    if (!container) return [];

    const focusableSelectors =
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    return Array.from(container.querySelectorAll(focusableSelectors)).filter(
      (el) => !el.hasAttribute("disabled"),
    );
  }

  focusFirstElement() {
    const container = this.isSheetOpen ? this.sheetContent : this.panel;
    if (!container) return;

    const focusable = this.getFocusableElements();
    if (focusable.length) {
      focusable[0].focus();
    } else {
      container.focus();
    }
  }

  handleDocumentClick(event) {
    const continueButton = event.target.closest("[data-gift-continue]");
    if (continueButton) {
      event.preventDefault();
      this.handleContinueClick(continueButton);
      return;
    }

    const removeTrigger = event.target.closest("[data-gift-summary-remove]");
    if (removeTrigger) {
      event.preventDefault();
      this.removeGiftWrap(removeTrigger);
      return;
    }

    const sheetTrigger = event.target.closest("[data-gift-sheet-trigger]");
    if (sheetTrigger) {
      event.preventDefault();
      this.openSheet(sheetTrigger);
      return;
    }

    if (event.target.closest("[data-gift-sheet-close]")) {
      event.preventDefault();
      this.closeSheet();
      return;
    }

    const trigger = event.target.closest("[data-gift-drawer-trigger]");
    if (trigger) {
      event.preventDefault();
      this.open(trigger);
      return;
    }

    if (event.target.closest("[data-gift-drawer-close]")) {
      event.preventDefault();
      this.close();
    }
  }

  handleKeyDown(event) {
    if (!this.isOpen) return;

    if (this.isSheetOpen && event.key === "Escape") {
      event.preventDefault();
      this.closeSheet();
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== "Tab") return;

    const focusable = this.getFocusableElements();
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      last.focus();
      event.preventDefault();
    } else if (!event.shiftKey && document.activeElement === last) {
      first.focus();
      event.preventDefault();
    }
  }

  handleVariantChange(event) {
    if (!event.target.matches('input[name="gift-wrap-option"]')) return;
    this.setStatus("");
    this.updateContinueButtonState();
  }

  handleContinueClick(button) {
    const selectedVariants = this.selectedVariantInputs;
    if (!selectedVariants.length) {
      const errorMessage =
        button.dataset.giftSelectionError || window.cartStrings?.error || "";
      this.setStatus(errorMessage);
      return;
    }

    this.setStatus("");
    this.setButtonLoading(button, true);
    this.pendingVariantIds = selectedVariants.map((i) => i.value);
    const summary = this.giftSummary;
    const hasExisting = summary?.dataset.giftSummaryPresent === "true";

    if (hasExisting) {
      this.pendingRemoval = {
        lineKey: summary.dataset.lineKey,
        lineIndex: summary.dataset.lineIndex,
      };
      this.removeGiftWrap(summary, { silent: true })
        .then((success) => {
          if (success) {
            this.addGiftWrap(button);
          } else {
            this.setButtonLoading(button, false);
          }
        })
        .catch(() => {
          this.setButtonLoading(button, false);
        });
    } else {
      this.addGiftWrap(button);
    }
  }

  addGiftWrap(button) {
    const variantIds = this.pendingVariantIds;
    if (!variantIds?.length) {
      this.setButtonLoading(button, false);
      return;
    }

    const sections = this.getSectionsToRender();
    const config = fetchConfig();
    config.body = JSON.stringify({
      items: variantIds.map((id) => ({ id: parseInt(id, 10), quantity: 1 })),
      ...(sections.length && { sections, sections_url: window.location.pathname }),
    });

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          const message = response.description || response.message || response.errors;
          this.setStatus(message);
          return;
        }

        if (typeof publish === "function") {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "gift-drawer",
            cartData: response,
          });
        }

        this.refreshCart(response);
        this.close();
      })
      .catch((error) => {
        console.error("[gift-drawer] add error", error);
        this.setStatus(window.cartStrings?.error || "Error");
      })
      .finally(() => {
        this.setButtonLoading(button, false);
        this.pendingVariantIds = [];
        this.pendingRemoval = null;
        this.clearSelection();
      });
  }

  removeGiftWrap(trigger, { silent = false } = {}) {
    const variantIds = trigger.dataset.variantIds
      ? trigger.dataset.variantIds.split(",").map((id) => parseInt(id, 10)).filter(Boolean)
      : trigger.dataset.variantId
        ? [parseInt(trigger.dataset.variantId, 10)]
        : [];

    if (!variantIds.length) return Promise.resolve(false);

    if (!silent) {
      trigger.setAttribute("aria-disabled", "true");
    }

    const updates = {};
    variantIds.forEach((id) => { updates[id] = 0; });

    const body = JSON.stringify({
      updates,
      sections: this.getSectionsToRender(),
      sections_url: window.location.pathname,
    });

    return fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);

        if (parsedState.errors) {
          if (!silent) this.setStatus(parsedState.errors);
          return false;
        }

        if (typeof publish === "function") {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "gift-drawer",
            cartData: parsedState,
          });
        }

        if (!silent) {
          this.refreshCart(parsedState);
        }
        return true;
      })
      .catch((error) => {
        console.error("[gift-drawer] remove error", error);
        if (!silent) this.setStatus(window.cartStrings?.error || "Error");
        return false;
      })
      .finally(() => {
        if (!silent) trigger.removeAttribute("aria-disabled");
      });
  }

  open(trigger) {
    const drawer = this.drawer;
    if (!drawer || this.isOpen) return;

    this.pendingVariantIds = trigger?.dataset.variantIds
      ? trigger.dataset.variantIds.split(",").filter(Boolean)
      : trigger?.dataset.variantId
        ? [trigger.dataset.variantId]
        : [];
    this.isOpen = true;
    this.activeTrigger = trigger;
    drawer.classList.add("is-open");
    drawer.setAttribute("aria-hidden", "false");

    requestAnimationFrame(() => {
      this.focusFirstElement();
      this.applyPendingSelection();
    });
  }

  close() {
    const drawer = this.drawer;
    if (!drawer || !this.isOpen) return;

    this.isOpen = false;
    drawer.classList.remove("is-open");
    drawer.setAttribute("aria-hidden", "true");
    this.closeSheet({ restoreFocus: false });
    this.setStatus("");

    if (this.activeTrigger) {
      this.activeTrigger.focus();
    }
  }

  updateContinueButtonState() {
    const button = this.continueButton;
    if (!button) return;

    const hasSelection = this.selectedVariantInputs.length > 0;
    button.disabled = !hasSelection;

    if (hasSelection) {
      button.removeAttribute("aria-disabled");
    } else {
      button.setAttribute("aria-disabled", "true");
    }
  }

  initSheetDrag() {
    const handle = this.sheetHandle;
    if (!handle) return;

    handle.addEventListener("pointerdown", this.startSheetDrag);
  }

  openSheet(trigger) {
    const sheet = this.sheet;
    const drawer = this.drawer;
    if (!sheet || !drawer) return;

    this.ensureSheetInBody();

    const title = trigger?.dataset.giftSheetTitle || "";
    const image = trigger?.dataset.giftSheetImage || "";
    const titleEl = this.sheetContent?.querySelector("[data-gift-sheet-title]");
    const imageEl = this.sheetContent?.querySelector("[data-gift-sheet-image]");

    if (titleEl && title) {
      titleEl.textContent = title;
    }

    if (imageEl && image) {
      imageEl.src = image;
      imageEl.alt = title || imageEl.alt || "";
    }

    this.isSheetOpen = true;
    this.activeSheetTrigger = trigger;

    drawer.classList.add("has-sheet-open");
    sheet.classList.add("is-open");
    sheet.setAttribute("aria-hidden", "false");

    requestAnimationFrame(() => {
      this.focusFirstElement();
    });
  }

  closeSheet({ restoreFocus = true } = {}) {
    const sheet = this.sheet;
    const drawer = this.drawer;
    if (!sheet || !this.isSheetOpen || !drawer) return;

    this.isSheetOpen = false;
    sheet.classList.remove("is-open");
    sheet.setAttribute("aria-hidden", "true");
    drawer.classList.remove("has-sheet-open");

    if (this.sheetContent) {
      this.sheetContent.style.transform = "";
      this.sheetContent.classList.remove("is-dragging");
    }

    if (this.sheetBackdrop) {
      this.sheetBackdrop.style.opacity = "";
    }

    if (restoreFocus && this.activeSheetTrigger) {
      this.activeSheetTrigger.focus();
    }

    this.activeSheetTrigger = null;
    this.restoreSheetToDrawer();
  }

  startSheetDrag(event) {
    if (!this.isSheetOpen) return;
    if (event.cancelable) event.preventDefault();

    this.sheetDragStartY = event.clientY ?? event.touches?.[0]?.clientY;
    this.sheetDragDelta = 0;

    document.addEventListener("pointermove", this.moveSheetDrag);
    document.addEventListener("pointerup", this.endSheetDrag);

    if (this.sheetContent) {
      this.sheetContent.classList.add("is-dragging");
    }
  }

  moveSheetDrag(event) {
    if (this.sheetDragStartY == null) return;
    if (event.cancelable) event.preventDefault();

    const currentY = event.clientY ?? event.touches?.[0]?.clientY;
    const delta = Math.max(0, currentY - this.sheetDragStartY);
    this.sheetDragDelta = delta;

    if (this.sheetContent) {
      this.sheetContent.style.transform = `translateY(${delta}px)`;
    }

    if (this.sheetBackdrop) {
      const opacity = Math.max(0, 1 - delta / 260);
      this.sheetBackdrop.style.opacity = opacity.toString();
    }
  }

  endSheetDrag() {
    if (this.sheetDragStartY == null) return;

    const delta = this.sheetDragDelta || 0;
    this.sheetDragStartY = null;
    this.sheetDragDelta = 0;

    document.removeEventListener("pointermove", this.moveSheetDrag);
    document.removeEventListener("pointerup", this.endSheetDrag);

    if (this.sheetContent) {
      this.sheetContent.style.transform = "";
      this.sheetContent.classList.remove("is-dragging");
    }

    if (this.sheetBackdrop) {
      this.sheetBackdrop.style.opacity = "";
    }

    if (delta > 80) {
      this.closeSheet();
    }
  }

  ensureSheetInBody() {
    const sheet = this.sheet;
    if (!sheet || sheet.parentElement === document.body) return;

    if (!this.sheetPlaceholder) {
      this.sheetPlaceholder = document.createComment("gift-sheet-placeholder");
    }

    sheet.parentElement.insertBefore(this.sheetPlaceholder, sheet);
    document.body.appendChild(sheet);
  }

  restoreSheetToDrawer() {
    const sheet = this.sheet;
    if (!sheet || !this.sheetPlaceholder) return;

    if (this.sheetPlaceholder.parentElement) {
      this.sheetPlaceholder.parentElement.insertBefore(sheet, this.sheetPlaceholder);
      this.sheetPlaceholder.remove();
    }
  }
}

document.addEventListener("DOMContentLoaded", () => {
  new GiftWrapDrawer();
});
